import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
	FiArrowLeft,
	FiSave,
	FiFileText,
	FiUser,
	FiMail,
	FiPhone,
	FiCalendar,
	FiMapPin,
	FiDollarSign,
	FiLink,
	FiCheck,
	FiX,
	FiImage,
	FiFile,
	FiHash,
	FiType,
	FiAlertCircle,
} from 'react-icons/fi';
import Card from '../../components/ui/card';
import Button from '../../components/ui/button';
import Input from '../../components/ui/input';
import Label from '../../components/ui/label';
import MapboxPlacesAutocomplete from '../../components/ui/mapbox-places-autocomplete';
import SublyApi from '../../helpers/Api';
import { parseFieldOptions } from '../../utils/formHelpers';

const CreateSubmission = () => {
	const history = useHistory();
	const token = useSelector((state) => state.admin.token);

	// Add ref to track file inputs
	const fileInputRefs = useRef({});

	// Temporary: Use hardcoded admin token for testing
	const testToken =
		'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJhZG1pbiI6InBlYW51dCIsImlzQXBwcm92ZWQiOnRydWUsImlhdCI6MTY0OTM4OTY0NX0.HhdqAZHDs0gCryngCOVCsZyqqk9xqeLmgwgkY5P7LqM';
	const actualToken = token || testToken;

	// State
	const [forms, setForms] = useState([]);
	const [users, setUsers] = useState([]);
	const [selectedForm, setSelectedForm] = useState(null);
	const [selectedUser, setSelectedUser] = useState(null);
	const [formData, setFormData] = useState({});
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [success, setSuccess] = useState('');
	const [validationModal, setValidationModal] = useState({
		show: false,
		message: '',
	});
	const [tempFileUrls, setTempFileUrls] = useState(new Set()); // Track temporary files

	// Heartbeat system to keep temp files alive while user is active
	useEffect(() => {
		if (tempFileUrls.size === 0) return;

		const sendHeartbeat = async () => {
			try {
				await SublyApi.request(
					'api/admin/forms/heartbeat',
					{ fileUrls: Array.from(tempFileUrls) },
					'post',
					{
						Authorization: `Bearer ${actualToken}`,
						'Content-Type': 'application/json',
					}
				);
				console.log('Heartbeat sent for', tempFileUrls.size, 'temporary files');
			} catch (err) {
				console.error('Heartbeat error:', err);
			}
		};

		// Send heartbeat every 10 minutes to keep files alive
		const heartbeatInterval = setInterval(sendHeartbeat, 10 * 60 * 1000);

		return () => {
			clearInterval(heartbeatInterval);
		};
	}, [tempFileUrls, actualToken]);

	// Cleanup temporary files when component unmounts or user navigates away
	useEffect(() => {
		const cleanupTempFiles = async () => {
			if (tempFileUrls.size === 0) return;

			try {
				// Delete all temporary files
				const deletePromises = Array.from(tempFileUrls).map(async (fileUrl) => {
					try {
						await SublyApi.request(
							'api/admin/forms/delete-file',
							{ fileUrl },
							'delete',
							{
								Authorization: `Bearer ${actualToken}`,
								'Content-Type': 'application/json',
							}
						);
					} catch (err) {
						console.error('Error deleting temp file on cleanup:', err);
					}
				});

				await Promise.all(deletePromises);
				console.log('Cleaned up temporary files on page unload');
			} catch (err) {
				console.error('Error during temp file cleanup:', err);
			}
		};

		// Handle browser close/refresh/navigation
		const handleBeforeUnload = (event) => {
			if (tempFileUrls.size > 0) {
				// Note: Modern browsers ignore custom messages, but we still need to call preventDefault
				event.preventDefault();
				// Trigger cleanup (this may not complete due to browser limitations)
				cleanupTempFiles();
				return (event.returnValue = ''); // Some browsers require this
			}
		};

		// Handle page visibility changes (tab switching, minimizing)
		const handleVisibilityChange = () => {
			if (document.hidden && tempFileUrls.size > 0) {
				// Page is now hidden, cleanup temp files
				cleanupTempFiles();
			}
		};

		// Handle React Router navigation
		const handleRouteChange = () => {
			cleanupTempFiles();
		};

		// Add event listeners
		window.addEventListener('beforeunload', handleBeforeUnload);
		document.addEventListener('visibilitychange', handleVisibilityChange);

		// Listen for React Router navigation
		const unblock = history.listen(handleRouteChange);

		// Cleanup function
		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
			document.removeEventListener('visibilitychange', handleVisibilityChange);
			unblock();
			// Final cleanup on component unmount
			cleanupTempFiles();
		};
	}, [actualToken, history]);

	// Fetch available forms
	useEffect(() => {
		const fetchForms = async () => {
			try {
				const response = await SublyApi.request('api/admin/forms', {}, 'get', {
					Authorization: `Bearer ${actualToken}`,
				});
				setForms(response.forms || []);
			} catch (err) {
				console.error('Error fetching forms:', err);
				setError('Failed to load forms');
			}
		};

		if (actualToken) {
			fetchForms();
		}
	}, [actualToken]);

	// Fetch available users
	useEffect(() => {
		const fetchUsers = async () => {
			try {
				const response = await SublyApi.request(
					'api/admin/forms/users',
					{},
					'get',
					{
						Authorization: `Bearer ${actualToken}`,
					}
				);
				setUsers(response.users || []);
			} catch (err) {
				console.error('Error fetching users:', err);
				setError('Failed to load users');
			}
		};

		if (actualToken) {
			fetchUsers();
		}
	}, [actualToken]);

	// Handle form selection
	const handleFormSelect = async (formId) => {
		if (!formId) {
			setSelectedForm(null);
			setFormData({});
			return;
		}

		try {
			const response = await SublyApi.request(
				`api/admin/forms/${formId}`,
				{},
				'get',
				{
					Authorization: `Bearer ${actualToken}`,
				}
			);
			console.log('Form data loaded:', response.form);
			console.log('Form fields:', response.form?.fields);
			console.log('Form sections:', response.form?.sections);

			setSelectedForm(response.form);

			// Initialize form data with empty values
			const initialData = {};

			// Initialize direct fields (not in sections)
			if (response.form?.fields) {
				response.form.fields.forEach((field) => {
					console.log(
						'Initializing direct field:',
						field.field_key,
						'type:',
						field.type
					);
					if (field.type?.toLowerCase() === 'address') {
						initialData[field.field_key] = {
							street: '',
							city: '',
							state: '',
							zip: '',
							coordinates: null,
							formatted_address: '',
						};
					} else {
						initialData[field.field_key] = '';
					}
				});
			}

			// Initialize section fields
			if (response.form?.sections) {
				response.form.sections.forEach((section) => {
					section.fields?.forEach((field) => {
						console.log(
							'Initializing section field:',
							field.field_key,
							'type:',
							field.type
						);
						if (field.type?.toLowerCase() === 'address') {
							initialData[field.field_key] = {
								street: '',
								city: '',
								state: '',
								zip: '',
								coordinates: null,
								formatted_address: '',
							};
						} else {
							initialData[field.field_key] = '';
						}
					});
				});
			}

			console.log('Initial form data:', initialData);
			setFormData(initialData);
		} catch (err) {
			console.error('Error fetching form:', err);
			setError('Failed to load form details');
		}
	};

	// Handle user selection
	const handleUserSelect = (username) => {
		if (!username) {
			setSelectedUser(null);
			return;
		}

		const user = users.find((u) => u.username === username);
		setSelectedUser(user);
	};

	// Handle input changes
	const handleInputChange = (fieldKey, value) => {
		setFormData((prev) => ({
			...prev,
			[fieldKey]: value,
		}));
	};

	// Handle file upload
	const handleFileUpload = async (fieldKey, file) => {
		try {
			console.log('Starting file upload for field:', fieldKey, 'file:', file);

			setFormData((prev) => ({
				...prev,
				[fieldKey]: 'Uploading...',
			}));

			const formDataObj = new FormData();
			formDataObj.append('file', file);

			console.log('Making file upload request...');

			const response = await SublyApi.request(
				'api/admin/forms/upload-file',
				formDataObj,
				'post',
				{
					Authorization: `Bearer ${actualToken}`,
					'Content-Type': 'multipart/form-data',
				}
			);

			console.log('File upload response:', response);

			setFormData((prev) => ({
				...prev,
				[fieldKey]: response.url,
			}));

			// Track temporary file URL for cleanup
			if (response.url && response.url.includes('temp-form-upload-')) {
				setTempFileUrls((prev) => new Set([...prev, response.url]));
			}
		} catch (err) {
			console.error('File upload error details:', {
				error: err,
				message: err?.message,
				response: err?.response,
				status: err?.status,
				data: err?.data,
			});

			setFormData((prev) => ({
				...prev,
				[fieldKey]: '',
			}));

			// Try to extract error message from various possible error structures
			let errorMessage = 'Failed to upload file';

			if (err?.message) {
				errorMessage = err.message;
			} else if (err?.response?.data?.message) {
				errorMessage = err.response.data.message;
			} else if (err?.data?.message) {
				errorMessage = err.data.message;
			} else if (err?.error?.message) {
				errorMessage = err.error.message;
			} else if (typeof err === 'string') {
				errorMessage = err;
			}

			setError(`Failed to upload file: ${errorMessage}`);
		}
	};

	// Handle file removal
	const handleFileRemove = async (fieldKey) => {
		const fileUrl = formData[fieldKey];

		if (!fileUrl || fileUrl === 'Uploading...') return;

		try {
			// Only delete if it's a temporary file (permanent files will be cleaned up by the system)
			if (fileUrl.includes('temp-form-upload-')) {
				console.log('Attempting to delete temp file:', fileUrl);

				await SublyApi.request(
					'api/admin/forms/delete-file',
					{ fileUrl },
					'delete',
					{
						Authorization: `Bearer ${actualToken}`,
						'Content-Type': 'application/json',
					}
				);

				console.log('File deleted successfully:', fileUrl);

				// Remove from tracking set since it's been manually deleted
				setTempFileUrls((prev) => {
					const newSet = new Set(prev);
					newSet.delete(fileUrl);
					return newSet;
				});
			}

			// Clear the field value
			setFormData((prev) => ({
				...prev,
				[fieldKey]: '',
			}));

			// Reset the file input to allow uploading the same file again
			if (fileInputRefs.current[fieldKey]) {
				fileInputRefs.current[fieldKey].value = '';
			}
		} catch (err) {
			console.error('File removal error details:', {
				error: err,
				message: err?.message,
				response: err?.response,
				status: err?.status,
				data: err?.data,
			});

			// Still clear the field value even if deletion fails
			setFormData((prev) => ({
				...prev,
				[fieldKey]: '',
			}));

			// Reset the file input even if deletion fails
			if (fileInputRefs.current[fieldKey]) {
				fileInputRefs.current[fieldKey].value = '';
			}

			// Extract error message using the same logic as file upload
			let errorMessage = 'Failed to remove file';

			if (typeof err === 'string') {
				errorMessage = err;
			} else if (Array.isArray(err)) {
				errorMessage = err.join(', ');
			} else if (err?.message) {
				errorMessage = err.message;
			} else if (err?.response?.data?.error?.message) {
				errorMessage = err.response.data.error.message;
			} else if (err?.response?.data?.message) {
				errorMessage = err.response.data.message;
			} else if (err?.data?.message) {
				errorMessage = err.data.message;
			} else if (err?.error?.message) {
				errorMessage = err.error.message;
			}

			setError(`Failed to remove file: ${errorMessage}`);
		}
	};

	// Helper function to detect file type from URL
	const getFileTypeFromUrl = (url) => {
		if (!url || typeof url !== 'string')
			return { isImage: false, isPdf: false, fileType: 'unknown' };

		const lowerUrl = url.toLowerCase();

		// Check for image extensions
		const imageExtensions = [
			'.jpg',
			'.jpeg',
			'.png',
			'.gif',
			'.webp',
			'.bmp',
			'.svg',
		];
		const isImage = imageExtensions.some((ext) => lowerUrl.includes(ext));

		// Check for PDF
		const isPdf = lowerUrl.includes('.pdf');

		// Determine file type
		let fileType = 'file';
		if (isImage) fileType = 'image';
		else if (isPdf) fileType = 'pdf';
		else if (lowerUrl.includes('.doc') || lowerUrl.includes('.docx'))
			fileType = 'document';
		else if (lowerUrl.includes('.xls') || lowerUrl.includes('.xlsx'))
			fileType = 'spreadsheet';
		else if (lowerUrl.includes('.ppt') || lowerUrl.includes('.pptx'))
			fileType = 'presentation';

		return { isImage, isPdf, fileType };
	};

	// Handle form submission
	const handleSubmit = async (e) => {
		e.preventDefault();

		if (!selectedForm) {
			setError('Please select a form first');
			return;
		}

		if (!selectedUser) {
			setError('Please select a user for this submission');
			return;
		}

		// Validate required fields
		const allFields = [
			...(selectedForm.fields || []),
			...(selectedForm.sections?.flatMap((section) => section.fields || []) ||
				[]),
		];

		const missingRequiredFields = [];

		allFields.forEach((field) => {
			if (field.is_required) {
				const value = formData[field.field_key];

				// Check if field is empty based on field type
				let isEmpty = false;

				if (value === null || value === undefined || value === '') {
					isEmpty = true;
				} else if (
					field.type?.toLowerCase() === 'address' &&
					typeof value === 'object'
				) {
					// For address fields, check if all components are empty
					const { street, city, state, zip, formatted_address } = value;
					isEmpty = !street && !city && !state && !zip && !formatted_address;
				} else if (Array.isArray(value)) {
					// For arrays (multiselect, checkbox), check if empty
					isEmpty = value.length === 0;
				}

				if (isEmpty) {
					missingRequiredFields.push(field.label);
				}
			}
		});

		if (missingRequiredFields.length > 0) {
			// Only show popup for multiple errors (3+), otherwise let inline validation and browser behavior handle it
			if (missingRequiredFields.length >= 3) {
				setValidationModal({
					show: true,
					message: `Please fill in the following required fields: ${missingRequiredFields.join(
						', '
					)}`,
				});
			}
			return;
		}

		setLoading(true);
		setError('');
		setSuccess('');

		try {
			// Create submission
			const response = await SublyApi.request(
				`api/admin/forms/submissions/create`,
				{
					form_id: selectedForm.id,
					data: formData,
					username: selectedUser.username,
				},
				'post',
				{
					Authorization: `Bearer ${actualToken}`,
				}
			);

			setSuccess('Submission created successfully!');

			// Clear temporary file tracking since files are now promoted to permanent storage
			setTempFileUrls(new Set());

			// Redirect to submission detail after a short delay
			setTimeout(() => {
				history.push(`/admin/submissions/${response.submission.id}`);
			}, 1500);
		} catch (err) {
			console.error('Submission error:', err);
			setError(err.message || 'Failed to create submission');
		} finally {
			setLoading(false);
		}
	};

	// Render field input based on type
	const renderFieldInput = (field) => {
		const fieldType = field.type?.toLowerCase();
		const value = formData[field.field_key] || '';

		console.log(
			'Rendering field:',
			field.field_key,
			'type:',
			fieldType,
			'value:',
			value
		);

		const options = parseFieldOptions(field.options);

		const inputProps = {
			value,
			onChange: (e) => handleInputChange(field.field_key, e.target.value),
			className:
				'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
			placeholder: field.placeholder || `Enter ${field.label.toLowerCase()}`,
			required: field.is_required,
		};

		switch (fieldType) {
			case 'email':
				return <input type='email' {...inputProps} />;

			case 'phone':
			case 'tel':
				return (
					<input
						type='tel'
						{...inputProps}
						pattern='[0-9\s\-\(\)\+\.]+'
						title='Please enter a valid phone number'
					/>
				);

			case 'number':
				return <input type='number' {...inputProps} />;

			case 'url':
				return (
					<input
						type='text'
						{...inputProps}
						placeholder={inputProps.placeholder || 'e.g. example.com'}
						onBlur={(e) => {
							const v = e.target.value.trim();
							if (v && !/^https?:\/\//i.test(v)) {
								handleInputChange(field.field_key, `https://${v}`);
							}
						}}
					/>
				);

			case 'date':
				return <input type='date' {...inputProps} />;

			case 'time':
				return <input type='time' {...inputProps} />;

			case 'datetime':
				return <input type='datetime-local' {...inputProps} />;

			case 'textarea':
				return <textarea {...inputProps} rows={3} />;

			case 'address':
				return (
					<MapboxPlacesAutocomplete
						id={field.field_key}
						name={field.field_key}
						value={value}
						onChange={(address) => {
							handleInputChange(field.field_key, {
								street: address.street || '',
								city: address.city || '',
								state: address.state || '',
								zip: address.zip || '',
								coordinates: address.coordinates || null,
								formatted_address: address.formatted_address || '',
							});
						}}
						placeholder={field.placeholder || 'Enter an address'}
						required={field.is_required}
						className='w-full'
					/>
				);

			case 'select':
			case 'radio':
				if (options.length > 0) {
					if (fieldType === 'radio') {
						// Radio buttons
						return (
							<div className='space-y-2'>
								{options.map((option, index) => {
									const optionValue = option.value || option;
									const optionLabel = option.label || option;
									return (
										<label key={index} className='flex items-center'>
											<input
												type='radio'
												name={field.field_key}
												value={optionValue}
												checked={value === optionValue}
												onChange={(e) =>
													handleInputChange(field.field_key, e.target.value)
												}
												className='mr-2'
												required={field.is_required}
											/>
											{optionLabel}
										</label>
									);
								})}
							</div>
						);
					} else {
						// Select dropdown
						return (
							<select {...inputProps}>
								<option value=''>Select an option</option>
								{options.map((option, index) => (
									<option key={index} value={option.value || option}>
										{option.label || option}
									</option>
								))}
							</select>
						);
					}
				}
				return <input type='text' {...inputProps} />;

			case 'multiselect':
				if (options.length > 0) {
					const selectedValues = Array.isArray(value) ? value : [];
					return (
						<div className='space-y-2 max-h-48 overflow-y-auto border border-gray-300 rounded-md p-3'>
							{options.map((option, index) => {
								const optionValue = option.value || option;
								const optionLabel = option.label || option;
								return (
									<label key={index} className='flex items-center'>
										<input
											type='checkbox'
											checked={selectedValues.includes(optionValue)}
											onChange={(e) => {
												let newValues;
												if (e.target.checked) {
													newValues = [...selectedValues, optionValue];
												} else {
													newValues = selectedValues.filter(
														(v) => v !== optionValue
													);
												}
												handleInputChange(field.field_key, newValues);
											}}
											className='mr-2'
										/>
										{optionLabel}
									</label>
								);
							})}
						</div>
					);
				}
				return (
					<input
						type='text'
						{...inputProps}
						placeholder='Enter values (comma separated)'
					/>
				);

			case 'checkbox':
				if (options.length > 0) {
					const selectedValues = Array.isArray(value) ? value : [];
					return (
						<div className='space-y-2'>
							{options.map((option, index) => {
								const optionValue = option.value || option;
								const optionLabel = option.label || option;
								return (
									<label key={index} className='flex items-center'>
										<input
											type='checkbox'
											checked={selectedValues.includes(optionValue)}
											onChange={(e) => {
												let newValues;
												if (e.target.checked) {
													newValues = [...selectedValues, optionValue];
												} else {
													newValues = selectedValues.filter(
														(v) => v !== optionValue
													);
												}
												handleInputChange(field.field_key, newValues);
											}}
											className='mr-2'
										/>
										{optionLabel}
									</label>
								);
							})}
						</div>
					);
				}
				return (
					<input
						type='text'
						{...inputProps}
						placeholder='Enter values (comma separated)'
					/>
				);

			case 'heading':
				// Display element - no input needed
				return (
					<div className='py-2'>
						<h3 className='text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2'>
							{field.label}
						</h3>
						{field.description && (
							<p className='text-sm text-gray-600 mt-2'>{field.description}</p>
						)}
					</div>
				);

			case 'paragraph':
				// Display element - no input needed
				return (
					<div className='py-2'>
						<div className='text-gray-700'>
							{field.description || field.label}
						</div>
					</div>
				);

			case 'divider':
				// Visual divider - no input needed
				return (
					<div className='py-4'>
						<hr className='border-gray-300' />
						{field.label && field.label !== 'Section Divider' && (
							<div className='text-center -mt-3'>
								<span className='bg-white px-4 text-sm text-gray-500'>
									{field.label}
								</span>
							</div>
						)}
					</div>
				);

			case 'file':
			case 'image':
				console.log('Rendering file/image field for:', field.field_key);
				const renderFilePreview = () => {
					if (!value || value === 'Uploading...') return null;

					const { isImage, isPdf, fileType } = getFileTypeFromUrl(value);

					if (isImage) {
						return (
							<div className='mb-3'>
								<div className='border border-gray-200 rounded-lg overflow-hidden bg-white p-4 relative'>
									{/* Remove button - Top right corner */}
									<button
										type='button'
										onClick={() => handleFileRemove(field.field_key)}
										className='absolute top-2 right-2 text-red-600 hover:text-red-800 p-1.5 rounded-full hover:bg-red-50 transition-colors z-10'>
										<FiX className='h-4 w-4' />
									</button>

									<div className='grid grid-cols-1 md:grid-cols-3 gap-4 items-center'>
										{/* Success message - Left side */}
										<div className='md:col-span-2'>
											<div className='flex items-center'>
												<FiCheck className='h-5 w-5 text-green-600 mr-3' />
												<div>
													<span className='text-sm text-green-700 font-medium block'>
														Image uploaded successfully
													</span>
													<button
														type='button'
														onClick={() => window.open(value, '_blank')}
														className='text-xs text-blue-600 hover:text-blue-800 hover:underline cursor-pointer bg-transparent border-none p-0'>
														Click to view full size
													</button>
												</div>
											</div>
										</div>

										{/* Square image preview - Right side */}
										<div className='flex justify-center md:justify-end'>
											<div className='w-20 h-20 rounded-lg overflow-hidden'>
												<img
													src={value}
													alt='Uploaded file preview'
													className='w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity block'
													onClick={() => window.open(value, '_blank')}
													onError={(e) => {
														e.target.style.display = 'none';
														e.target.nextSibling.style.display = 'flex';
													}}
												/>
												<div
													style={{ display: 'none' }}
													className='flex items-center justify-center w-full h-full text-gray-400 bg-gray-50'>
													<FiImage className='h-6 w-6' />
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						);
					} else if (isPdf) {
						return (
							<div className='mb-3'>
								<div className='border border-gray-200 rounded-lg overflow-hidden bg-white p-4 relative'>
									{/* Remove button - Top right corner */}
									<button
										type='button'
										onClick={() => handleFileRemove(field.field_key)}
										className='absolute top-2 right-2 text-red-600 hover:text-red-800 p-1.5 rounded-full hover:bg-red-50 transition-colors z-10'>
										<FiX className='h-4 w-4' />
									</button>

									<div className='grid grid-cols-1 md:grid-cols-3 gap-4 items-center'>
										{/* Success message - Left side */}
										<div className='md:col-span-2'>
											<div className='flex items-center'>
												<FiCheck className='h-5 w-5 text-green-600 mr-3' />
												<div>
													<span className='text-sm text-green-700 font-medium block'>
														PDF uploaded successfully
													</span>
													<a
														href={value}
														target='_blank'
														rel='noopener noreferrer'
														className='text-xs text-blue-600 hover:text-blue-800 hover:underline'>
														Click to view PDF →
													</a>
												</div>
											</div>
										</div>

										{/* PDF icon - Right side */}
										<div className='flex justify-center md:justify-end'>
											<div className='w-20 h-20 rounded-lg overflow-hidden bg-red-50 flex items-center justify-center'>
												<FiFile className='h-8 w-8 text-red-500' />
											</div>
										</div>
									</div>
								</div>
							</div>
						);
					} else {
						return (
							<div className='mb-3'>
								<div className='border border-gray-200 rounded-lg overflow-hidden bg-white p-4 relative'>
									{/* Remove button - Top right corner */}
									<button
										type='button'
										onClick={() => handleFileRemove(field.field_key)}
										className='absolute top-2 right-2 text-red-600 hover:text-red-800 p-1.5 rounded-full hover:bg-red-50 transition-colors z-10'>
										<FiX className='h-4 w-4' />
									</button>

									<div className='grid grid-cols-1 md:grid-cols-3 gap-4 items-center'>
										{/* Success message - Left side */}
										<div className='md:col-span-2'>
											<div className='flex items-center'>
												<FiCheck className='h-5 w-5 text-green-600 mr-3' />
												<div>
													<span className='text-sm text-green-700 font-medium block'>
														File uploaded successfully
													</span>
													<a
														href={value}
														target='_blank'
														rel='noopener noreferrer'
														className='text-xs text-blue-600 hover:text-blue-800 hover:underline'>
														Click to download →
													</a>
												</div>
											</div>
										</div>

										{/* File icon - Right side */}
										<div className='flex justify-center md:justify-end'>
											<div className='w-20 h-20 rounded-lg overflow-hidden bg-gray-50 flex items-center justify-center'>
												<FiFile className='h-8 w-8 text-gray-500' />
											</div>
										</div>
									</div>
								</div>
							</div>
						);
					}
				};

				return (
					<div className='w-full space-y-3'>
						{renderFilePreview()}
						{value === 'Uploading...' && (
							<div className='flex items-center p-3 bg-blue-50 rounded-lg border border-blue-200'>
								<div className='animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full mr-3'></div>
								<span className='text-sm text-blue-700'>Uploading file...</span>
							</div>
						)}

						{/* File upload area with improved styling */}
						<div className='w-full border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors bg-gray-50 hover:bg-gray-100'>
							<div className='flex flex-col items-center space-y-4'>
								<div className='p-3 bg-gray-200 rounded-full'>
									<svg
										xmlns='http://www.w3.org/2000/svg'
										className='h-8 w-8 text-gray-500'
										fill='none'
										viewBox='0 0 24 24'
										stroke='currentColor'>
										<path
											strokeLinecap='round'
											strokeLinejoin='round'
											strokeWidth={1.5}
											d='M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12'
										/>
									</svg>
								</div>
								<div className='text-center'>
									<label
										htmlFor={field.field_key}
										className='cursor-pointer inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-md text-white bg-gray-900 hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2 transition-colors'>
										Choose File
									</label>
									<input
										id={field.field_key}
										ref={(el) => (fileInputRefs.current[field.field_key] = el)}
										type='file'
										accept={fieldType === 'image' ? 'image/*' : '*'}
										onChange={(e) => {
											const file = e.target.files?.[0];
											if (file) {
												// Client-side file size validation
												const maxSize = 30 * 1024 * 1024; // 30MB
												if (file.size > maxSize) {
													setError(
														`File "${file.name}" is too large. Maximum size is 30MB.`
													);
													e.target.value = ''; // Clear the input
													return;
												}
												handleFileUpload(field.field_key, file);
											}
										}}
										className='sr-only'
									/>
									<p className='text-sm text-gray-600 mt-3'>
										{fieldType === 'image'
											? 'Upload images only'
											: 'Upload any file type'}
									</p>
									<p className='text-xs text-gray-500 mt-1'>
										Maximum file size: 30MB
									</p>
								</div>
							</div>
						</div>
					</div>
				);

			default:
				console.log(
					'Using default input for field:',
					field.field_key,
					'type:',
					fieldType
				);
				return <input type='text' {...inputProps} />;
		}
	};

	return (
		<div className='container mx-auto px-4 py-8 max-w-4xl'>
			{/* Header */}
			<div className='flex items-center justify-between mb-6'>
				<div className='flex items-center'>
					<Button
						variant='outline'
						onClick={() => history.push('/admin/submissions')}
						className='mr-4'>
						<FiArrowLeft className='h-4 w-4 mr-2' />
						Back to Submissions
					</Button>
					<div>
						<h1 className='text-2xl font-bold text-gray-900'>
							Create New Submission
						</h1>
						<p className='text-sm text-gray-500 mt-1'>
							Fill out a form submission on behalf of a client
						</p>
					</div>
				</div>
			</div>

			{/* Error/Success Messages */}
			{error && (
				<div className='bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6'>
					<div className='flex items-center'>
						<FiAlertCircle className='h-5 w-5 mr-2' />
						{error}
					</div>
				</div>
			)}

			{success && (
				<div className='bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6'>
					<div className='flex items-center'>
						<FiCheck className='h-5 w-5 mr-2' />
						{success}
					</div>
				</div>
			)}

			{/* Form Selection */}
			<Card className='mb-6'>
				<Card.Header>
					<h2 className='text-lg font-medium'>1. Select Form</h2>
				</Card.Header>
				<Card.Content>
					<div>
						<Label htmlFor='form-select'>Choose a form to fill out:</Label>
						<select
							id='form-select'
							value={selectedForm?.id || ''}
							onChange={(e) => handleFormSelect(e.target.value)}
							className='mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'>
							<option value=''>Select a form...</option>
							{forms.map((form) => (
								<option key={form.id} value={form.id}>
									{form.name}
								</option>
							))}
						</select>
					</div>
				</Card.Content>
			</Card>

			{/* User Selection */}
			<Card className='mb-6'>
				<Card.Header>
					<h2 className='text-lg font-medium'>2. Select User</h2>
				</Card.Header>
				<Card.Content>
					<div>
						<Label htmlFor='user-select'>
							Choose the user this submission is for:
						</Label>
						<select
							id='user-select'
							value={selectedUser?.username || ''}
							onChange={(e) => handleUserSelect(e.target.value)}
							className='mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent'>
							<option value=''>Select a user...</option>
							{users.map((user) => (
								<option key={user.username} value={user.username}>
									{user.display_name}
								</option>
							))}
						</select>
						{selectedUser && (
							<div className='mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-sm'>
								<div className='font-medium text-blue-900'>Selected User:</div>
								<div className='text-blue-700'>
									{selectedUser.first_name} {selectedUser.last_name} (
									{selectedUser.username})
								</div>
								<div className='text-blue-600'>{selectedUser.email}</div>
							</div>
						)}
					</div>
				</Card.Content>
			</Card>

			{/* Form Fields */}
			{selectedForm && selectedUser && (
				<form onSubmit={handleSubmit}>
					<Card className='mb-6'>
						<Card.Header>
							<h2 className='text-lg font-medium'>
								3. Fill Out Form: {selectedForm.name}
							</h2>
							{selectedForm.description && (
								<p className='text-sm text-gray-600 mt-1'>
									{selectedForm.description}
								</p>
							)}
						</Card.Header>
						<Card.Content>
							<div className='space-y-6'>
								{/* Render direct fields (not in sections) */}
								{selectedForm.fields && selectedForm.fields.length > 0 && (
									<div className='w-full space-y-6'>
										{selectedForm.fields.map((field) => {
											// Check if this is a display-only field
											const isDisplayField = [
												'heading',
												'paragraph',
												'divider',
											].includes(field.type?.toLowerCase());

											if (isDisplayField) {
												// Render display fields without labels or input wrapper
												return (
													<div key={field.id} className='w-full'>
														{renderFieldInput(field)}
													</div>
												);
											}

											// Regular input fields
											return (
												<div key={field.id} className='w-full'>
													<Label htmlFor={field.field_key}>
														{field.label}
														{field.is_required && (
															<span className='text-red-500 ml-1'>*</span>
														)}
													</Label>
													{field.description && (
														<p className='text-xs text-gray-500 mt-1 mb-2'>
															{field.description}
														</p>
													)}
													<div className='mt-2'>{renderFieldInput(field)}</div>
												</div>
											);
										})}
									</div>
								)}

								{/* Render sections with their fields */}
								{selectedForm.sections?.map((section) => (
									<div key={section.id} className='space-y-4'>
										<div className='border-b border-gray-200 pb-2'>
											<h3 className='text-lg font-medium text-gray-900'>
												{section.title}
											</h3>
											{section.description && (
												<p className='text-sm text-gray-600 mt-1'>
													{section.description}
												</p>
											)}
										</div>

										<div className='w-full space-y-6'>
											{section.fields?.map((field) => {
												// Check if this is a display-only field
												const isDisplayField = [
													'heading',
													'paragraph',
													'divider',
												].includes(field.type?.toLowerCase());

												if (isDisplayField) {
													// Render display fields without labels or input wrapper
													return (
														<div key={field.id} className='w-full'>
															{renderFieldInput(field)}
														</div>
													);
												}

												// Regular input fields
												return (
													<div key={field.id} className='w-full'>
														<Label htmlFor={field.field_key}>
															{field.label}
															{field.is_required && (
																<span className='text-red-500 ml-1'>*</span>
															)}
														</Label>
														{field.description && (
															<p className='text-xs text-gray-500 mt-1 mb-2'>
																{field.description}
															</p>
														)}
														<div className='mt-2'>
															{renderFieldInput(field)}
														</div>
													</div>
												);
											})}
										</div>
									</div>
								))}
							</div>
						</Card.Content>
					</Card>

					{/* Submit Button */}
					<div className='flex justify-end'>
						<Button
							type='submit'
							disabled={loading || !selectedForm || !selectedUser}
							className='bg-blue-600 hover:bg-blue-700 text-white'>
							{loading ? (
								<>
									<div className='animate-spin h-4 w-4 border border-white border-t-transparent rounded-full mr-2'></div>
									Creating Submission...
								</>
							) : (
								<>
									<FiSave className='h-4 w-4 mr-2' />
									Create Submission
								</>
							)}
						</Button>
					</div>
				</form>
			)}

			{/* Validation Modal */}
			{validationModal.show && (
				<div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
					<div className='bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl'>
						<div className='flex items-center mb-4'>
							<FiAlertCircle className='h-6 w-6 text-red-500 mr-3' />
							<h3 className='text-lg font-semibold text-gray-900'>
								Required Fields Missing
							</h3>
						</div>
						<p className='text-gray-700 mb-6'>{validationModal.message}</p>
						<div className='flex justify-end'>
							<Button
								onClick={() => setValidationModal({ show: false, message: '' })}
								className='bg-red-600 hover:bg-red-700 text-white'>
								OK, I'll fill them in
							</Button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default CreateSubmission;
